import { useMutation } from '@tanstack/react-query';
import {
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { TemplateConfig } from '@lg/shared';
import { useApi } from '../api/client';
import { errorMessage, saveFile } from '../ui/format';
import { D365_WORKFLOW_TEMPLATES, fetchD365WorkflowFile } from './d365Workflows';
import { WorkflowExportRetarget } from './WorkflowExportRetarget';

const useStyles = makeStyles({
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  list: { display: 'flex', flexDirection: 'column', borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  what: { display: 'flex', flexDirection: 'column', flex: '1 1 260px', minWidth: 0 },
  actions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
});

/**
 * Workflow hand-off for the venue company: one XML per selected workflow with
 * its routing, the venue's limits and the D365 import steps. Imported by hand.
 */
export function WorkflowFiles({
  requestId,
  legalEntityId,
  template,
  selected,
  beforeDownload,
}: {
  requestId: string;
  legalEntityId: string | null;
  template: TemplateConfig;
  selected: readonly string[];
  /** Saves unsaved answers first, so the file matches the screen. */
  beforeDownload: () => Promise<unknown>;
}) {
  const styles = useStyles();
  const api = useApi();
  const roleName = new Map(template.roleCatalog.map((r) => [r.roleId, r.name]));
  const download = useMutation({
    mutationFn: async (key?: string) => {
      await beforeDownload();
      saveFile(await api.workflowFile(requestId, key));
    },
  });
  // The working D365 export from the template company, saved for this company (no upload needed).
  const d365 = useMutation({
    mutationFn: async (documentType: string) => {
      if (!legalEntityId) throw new Error('Enter the legal entity ID first');
      saveFile(await fetchD365WorkflowFile(documentType, legalEntityId));
    },
  });
  const workflows = template.workflowLibrary.filter((w) => selected.includes(w.key));

  const summary = (type: string): string => {
    const route = template.routing.documentTypes.find((d) => d.type === type);
    if (!route) return '';
    const steps = route.steps.map((s) =>
      s.kind === 'role'
        ? `${s.label}: ${roleName.get(s.roleId) ?? s.roleId}`
        : `${s.label}: manager chain up to the signing limit`,
    );
    return `${steps.join(' → ')} · escalates to ${roleName.get(route.escalationRoleId) ?? route.escalationRoleId}`;
  };

  return (
    <>
      <Card className={styles.card}>
        <Subtitle1 as="h2">Workflow files{legalEntityId ? ` for ${legalEntityId}` : ''}</Subtitle1>
        <Body1>
          Each workflow is copied from {template.templateLegalEntityId} with D365's own export and import. The
          file says where it goes and how it routes, lists {legalEntityId ?? 'the venue'}'s approval limits
          and role holders, and gives the admin the import steps. Provisioning tracks the import as a manual
          task.
        </Body1>
        {!legalEntityId && (
          <MessageBar intent="info">Enter the legal entity ID to download the files.</MessageBar>
        )}
        <div className={styles.list}>
          {workflows.map((w) => (
            <div key={w.key} className={styles.row}>
              <div className={styles.what}>
                <b>{w.name}</b>
                <Caption1>{summary(w.documentType)}</Caption1>
                {D365_WORKFLOW_TEMPLATES[w.documentType] && (
                  <Caption1>
                    D365 workflow file: {D365_WORKFLOW_TEMPLATES[w.documentType]?.file} from{' '}
                    {D365_WORKFLOW_TEMPLATES[w.documentType]?.from}, for Workflows → Import in{' '}
                    {legalEntityId ?? 'the venue company'} after the ledger package (it creates work calendar{' '}
                    {D365_WORKFLOW_TEMPLATES[w.documentType]?.calendar}, which the workflow's due dates use).
                  </Caption1>
                )}
              </div>
              {D365_WORKFLOW_TEMPLATES[w.documentType] && (
                <Button
                  size="small"
                  appearance="primary"
                  disabled={!legalEntityId || d365.isPending}
                  onClick={() => d365.mutate(w.documentType)}
                >
                  Download D365 workflow (.xml)
                </Button>
              )}
              <Button
                size="small"
                disabled={!legalEntityId || download.isPending}
                onClick={() => download.mutate(w.key)}
              >
                Download XML
              </Button>
            </div>
          ))}
          {workflows.length === 0 && <Caption1>No workflows selected.</Caption1>}
        </div>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!legalEntityId || workflows.length === 0 || download.isPending}
            onClick={() => download.mutate(undefined)}
          >
            Download all (.zip)
          </Button>
        </div>
        {download.isError && <MessageBar intent="error">{errorMessage(download.error)}</MessageBar>}
        {d365.isError && <MessageBar intent="error">{errorMessage(d365.error)}</MessageBar>}
      </Card>
      <WorkflowExportRetarget legalEntityId={legalEntityId} />
    </>
  );
}
